import React, { useEffect, useRef } from "react";
import Lenis from "lenis";
import { motion, useReducedMotion } from "framer-motion";

/* Smooth momentum scrolling via Lenis */
export const SmoothScroll = ({ children }) => {
  const reduce = useReducedMotion();
  useEffect(() => {
    if (reduce) return;
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      lerp: 0.09,
    });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    // anchor smooth scroll
    const onClick = (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      const id = a.getAttribute("href");
      if (id.length < 2) return;
      const el = document.querySelector(id);
      if (el) {
        e.preventDefault();
        lenis.scrollTo(el, { offset: -84 });
      }
    };
    document.addEventListener("click", onClick);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      document.removeEventListener("click", onClick);
    };
  }, [reduce]);
  return children;
};

export const EASE = [0.16, 1, 0.3, 1];

export const fadeUp = {
  hidden: { opacity: 0, y: 34 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: EASE } },
};

export const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.05 } },
};

/* Section reveal wrapper */
export const Reveal = ({ children, className, delay = 0, as = "div" }) => {
  const M = motion[as] || motion.div;
  return (
    <M
      className={className}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.2 }}
      variants={{
        hidden: { opacity: 0, y: 34 },
        show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: EASE, delay } },
      }}
    >
      {children}
    </M>
  );
};

/* Masked line-by-line hero reveal */
export const MaskLines = ({ lines, className, lineClassName }) => {
  return (
    <span className={className}>
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden">
          <motion.span
            className={"block " + (lineClassName || "")}
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1.05, ease: EASE, delay: 0.35 + i * 0.16 }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </span>
  );
};

/* Parallax value hook */
export const useParallaxRef = () => useRef(null);
